// ONE ACCOUNT ON THE WEBSITE (ACC-3a): WHETHER THE SERVER HAS IT ON, AND WHO THIS BROWSER IS SIGNED IN AS.
//
// THE SWITCH IS THE SERVER'S. `v2Credits` `get` answers `capabilities.webAccounts` (contract 2.17.0), which is the
// server's WEB_ACCOUNTS_ENABLED read the one way (`functions/src/v2/account/flag.ts`). The shell already makes that call
// for its chrome, so the answer rides on it: no second call, and no build variable of the client's own. Until it has
// answered, a screen that depends on it shows the start-up spinner and nothing else; if it could not answer, the site is
// what it was before accounts — the switch's own default, and the only state a shipped build has been in.
//
// OFF IS TODAY, EXACTLY: `/account` is the assent page, `/checkout` sells to whoever the server sells to, the header's
// chip is "Account" to `/delete-account`, and `/signin`, `/forgot` and `/account/action` are the unknown routes they were
// (the entry). `test/accountScreens.test.tsx` "flag off" holds each of those.
//
// WHO IS SIGNED IN IS THE AUTH SEAM'S (`services/auth.ts`, ACC-2), read live through its getters on every render. A
// look-around (anonymous) and nobody are both "signed out" to an owner; a password account and an Apple account are
// "signed in". A screen that changes who is signed in calls `session.refresh()` after, which re-renders every reader.

/* eslint-disable react-refresh/only-export-components --
   The context, its provider and the hooks that read it belong together, as `session.tsx`'s do. */

import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import type { AuthSession } from "../services";
import { useSession } from "./session";

/** true / false once `v2Credits` `get` has answered; null while it has not. A failed `get` is false: today's site. */
export type WebAccounts = boolean | null;

const WebAccountsContext = createContext<WebAccounts>(false);

export function WebAccountsProvider({ value, children }: { value: WebAccounts; children: ReactNode }) {
  return <WebAccountsContext.Provider value={value}>{children}</WebAccountsContext.Provider>;
}

/** The server's WEB_ACCOUNTS_ENABLED as this page last heard it, or null before it has heard. */
export function useWebAccounts(): WebAccounts {
  return useContext(WebAccountsContext);
}

/** Signed in to an ACCOUNT: a password or Apple. A look-around and nobody are signed out. */
export function signedIn(auth: Pick<AuthSession, "provider">): boolean {
  return auth.provider === "password" || auth.provider === "apple";
}

/** A password account whose email is not yet confirmed: the one account the checkout holds back (C7). Apple accounts
 *  arrive confirmed, and a password added to an Apple account does not un-confirm it. */
export function needsEmailConfirmed(auth: Pick<AuthSession, "provider" | "emailVerified">): boolean {
  return auth.provider === "password" && !auth.emailVerified;
}

/**
 * THE START-UP LOOK, AS A COMPONENT: the same spinner `index.html` shows before the first screen, for a screen waiting on
 * the switch or on an account step. NO WORDS (ACC-3a): `aria-busy` says it is working; the page around it names it.
 */
export function Busy() {
  return (
    <div className="busy-page" aria-busy="true" data-busy>
      <span className="spinner" aria-hidden="true" />
    </div>
  );
}

/** A route that changes with the switch: `on` with it on, `off` (today's element) with it off. While the server has not
 *  said, `pending`: today's element where one exists at this path, so the switch off is today's page from its first
 *  frame; the spinner for a path that did not exist before. */
export function AccountsRoute({ on, off, pending = <Busy /> }: { on: ReactNode; off: ReactNode; pending?: ReactNode }) {
  const webAccounts = useWebAccounts();
  if (webAccounts === null) return <>{pending}</>;
  return <>{webAccounts ? on : off}</>;
}

/** What this browser's sign-in looks like, for noticing a change the page did not make. */
function authSnapshot(auth: AuthSession): string {
  return `${auth.uid ?? ""}|${auth.provider ?? ""}|${auth.emailVerified ? 1 : 0}`;
}

/**
 * WHEN THE OWNER COMES BACK TO THIS TAB, RE-READ THE SIGN-IN (ACC-3a). Two things happen elsewhere and show only here:
 * the confirm-email link opened in another tab (C7 says "Open it, then come back to this page", so coming back is the
 * cue), and the account ending elsewhere — deleted in the app, or its sign-in revoked (F3). `auth.refresh()` reloads the
 * user and forces a fresh token, so the server sees the same `email_verified` this page does; the page re-renders only
 * when who is signed in, or their confirmation, actually changed. Only with the switch on, and only for an account:
 * with it off nothing here runs, as before.
 */
export function useAuthWatch(active: boolean): void {
  const session = useSession();
  const { auth, refresh } = session;
  const running = useRef(false);
  useEffect(() => {
    if (!active) return;
    const check = () => {
      if (running.current || !signedIn(auth)) return;
      running.current = true;
      const before = authSnapshot(auth);
      void auth.refresh().finally(() => {
        running.current = false;
        if (authSnapshot(auth) !== before) refresh();
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, auth, refresh]);
}

/**
 * THE SIGN-IN ENDED UNDER THIS PAGE (F3): it was an account at some point in this page's life and is not now, and this
 * page did not end it. The account was deleted in the app, or its sign-in was revoked. A page load that starts signed
 * out has seen no account and never says so.
 */
export function useSignInEnded(active: boolean): boolean {
  const { auth } = useSession();
  const seen = useRef(false);
  if (!active) return false;
  if (signedIn(auth)) {
    seen.current = true;
    return false;
  }
  return seen.current;
}
